import { Body2 } from '@components/typography/Body2.tsx';
import { Icon } from '@components/Icon.tsx';
import { IconType } from '@src/IconType.ts';
import { classNames } from '@src/utils/classNames';
import {
  forwardRef,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
} from 'react';

export const Checkbox = forwardRef<
  HTMLInputElement,
  React.InputHTMLAttributes<HTMLInputElement> & {
    indeterminate?: boolean;
    label?: ReactNode;
    labelClassName?: string;
  }
>(({ indeterminate, label, labelClassName, ...props }, forwardedRef) => {
  const ref = useRef<HTMLInputElement | null>(null);
  const updateIndeterminate = useCallback(() => {
    if (ref.current) {
      ref.current.indeterminate = indeterminate ?? false;
    }
  }, [indeterminate]);

  useEffect(updateIndeterminate, [updateIndeterminate]);

  return (
    <label
      className={classNames(
        'rounded-md inline-flex items-center',
        props.disabled ? 'cursor-not-allowed' : 'cursor-pointer',
      )}
    >
      <input
        {...props}
        ref={(instance) => {
          ref.current = instance;
          if (typeof forwardedRef === 'function') {
            forwardedRef(instance);
          } else if (forwardedRef !== null) {
            forwardedRef.current = instance;
          }
          updateIndeterminate();
        }}
        className="hidden"
        type="checkbox"
      />
      {/* Render both icons so that the image is already loaded when switching the state */}
      <Icon
        type={IconType.CheckboxChecked}
        className={classNames(
          'w-6 h-6 text-blue-500 hidden',
          props.checked && '!block',
        )}
      />
      <Icon
        type={IconType.CheckboxEmpty}
        className={classNames(
          'w-6 h-6 text-blue-500 hidden',
          !props.checked && '!block',
        )}
      />
      <Body2 className={classNames('ml-2.5', labelClassName)}>{label}</Body2>
    </label>
  );
});
